import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import { max, min, bisectLeft } from 'd3-array';
import { throttle } from 'lodash-es';
import { color } from '@strava/ui-tokens/js';

import Area from './Area';
import Axis from './Axis';
import Tooltip from './Tooltip';
import SurfaceTypePath from './SurfaceTypePath';
import styles from './ElevationChart.module.scss';

class ElevationChart extends Component {
  state = {
    stream: null,
    xScale: null,
    yScale: null,
    height: 0,
    width: 0,
    markerPosition: null,
    tooltipContent: null
  };

  container = React.createRef();

  formatters = null;

  node = React.createRef();

  hitbox = React.createRef();

  handleResize = throttle(() => {
    this.buildScales();
  }, 250);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.updateFormatters();
    this.buildScales();
  }

  componentDidUpdate({
    elevation: prevElevation,
    distance: prevDistance,
    surface: prevSurface,
    units: prevUnits,
    scopeTo: prevScopeTo,
    hoveredDistance: prevHoveredDistance
  }) {
    const { distance, elevation, units, hoveredDistance, scopeTo, surface } =
      this.props;
    const elevationChanged = prevElevation.length !== elevation.length;
    const distanceChanged = prevDistance.length !== distance.length;
    const surfaceChanged = prevSurface?.length !== surface?.length;
    const unitsChanged = prevUnits !== units;
    const hasScopeChanged =
      prevScopeTo?.[0] !== scopeTo?.[0] || prevScopeTo?.[1] !== scopeTo?.[1];

    if (unitsChanged) {
      this.updateFormatters();
    }

    if (elevationChanged || distanceChanged || unitsChanged || surfaceChanged) {
      this.buildScales();
    }

    if (hasScopeChanged) {
      this.scopeTo(scopeTo);
    }

    if (prevHoveredDistance !== hoveredDistance) {
      if (hoveredDistance === null) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ markerPosition: null, tooltipContent: null });
      } else {
        const streamIndex = this.getStreamIndex(hoveredDistance);
        if (streamIndex !== -1) {
          this.updateTooltipContent(streamIndex, hoveredDistance);
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  getStreamIndex = (distance) => {
    const { stream } = this.state;
    return Math.min(
      bisectLeft(stream.distance, distance),
      stream.distance.length - 1
    );
  };

  // TODO: hover got really slow on long streams
  updateMarker = (e) => {
    const { onUpdateMarker } = this.props;
    const { xScale } = this.state;
    const { distanceFormatter } = this.formatters;
    const bounds = this.hitbox.current.getBoundingClientRect();
    const cursorPosition = e.clientX - bounds.left;
    const distCursor = xScale.invert(cursorPosition);
    // distCursor at this point will be in kilometers or miles so convert distance back to meters
    // as stream.distance is in meters so we want to be consistent
    const distance = distCursor / distanceFormatter.convert(1);
    const streamIndex = this.getStreamIndex(distance);

    if (streamIndex === -1 || Number.isNaN(distance)) {
      return;
    }

    onUpdateMarker(streamIndex, { distance });
    this.updateTooltipContent(streamIndex, distance);
  };

  updateTooltipContent = (streamIndex, distance) => {
    const {
      tooltipDistanceLabel,
      tooltipElevationLabel,
      tooltipGradeLabel,
      tooltipSurfaceLabel,
      tooltipSurfaceTypes
    } = this.props;
    const { stream, xScale } = this.state;
    const { elevationFormatter, distanceFormatter, gradePercentFormatter } =
      this.formatters;

    const distanceValue = stream.distance[streamIndex];
    const elevationValue = stream.elevation[streamIndex];
    const gradeValue = this.gradeAtIndex(streamIndex);
    const surfaceValue = stream?.surface?.[streamIndex] ?? null;

    const tooltipContent = {
      distance: {
        label: tooltipDistanceLabel,
        value: distanceFormatter.formatShort(distanceValue, 2)
      },
      elevation: {
        label: tooltipElevationLabel,
        value: elevationFormatter.formatShort(elevationValue)
      },
      grade: {
        label: tooltipGradeLabel,
        value: gradePercentFormatter.formatShort(gradeValue)
      }
    };

    if (surfaceValue) {
      let surfaceTypeValue;
      if ([1, 'Paved'].includes(surfaceValue)) {
        surfaceTypeValue = tooltipSurfaceTypes.paved;
      } else if ([2, 'Unpaved'].includes(surfaceValue)) {
        surfaceTypeValue = tooltipSurfaceTypes.unpaved;
      } else {
        surfaceTypeValue = tooltipSurfaceTypes.unknown;
      }

      tooltipContent.surface = {
        label: tooltipSurfaceLabel,
        value: surfaceTypeValue
      };
    }

    this.setState({
      // distance at this point will be in meeters or feet and we need to convert it to
      // kilometers or miles so that we can get the x position of the marker
      markerPosition: xScale(distance * distanceFormatter.convert(1)),
      tooltipContent
    });
  };

  handleMouseLeave = (e) => {
    const { onUpdateMarker, onMouseLeave } = this.props;
    onUpdateMarker(null, { distance: null });
    onMouseLeave(e);
    this.setState({ markerPosition: null, tooltipContent: null });
  };

  updateFormatters() {
    const { units, formatters } = this.props;

    this.formatters = {
      elevationFormatter: new formatters.ElevationFormatter(units),
      distanceFormatter: new formatters.DistanceFormatter(units),
      gradePercentFormatter: new formatters.PercentFormatter()
    };
  }

  buildScales() {
    const {
      margin,
      height: heightProp,
      elevation,
      distance,
      surface
    } = this.props;
    const { elevationFormatter, distanceFormatter } = this.formatters;

    const elevationOrDefault = elevation.length === 0 ? [0, 100] : elevation;

    const convertedElevation = elevationOrDefault.map((elev) =>
      elevationFormatter.convert(elev)
    );
    const convertedDistance = distance.map((dist) =>
      distanceFormatter.convert(dist)
    );

    const width =
      this.container.current.offsetWidth - margin.left - margin.right;
    const height = heightProp - margin.top - margin.bottom;
    const yMax = max(convertedElevation);
    const yMin = min(convertedElevation);
    const xScale = scaleLinear().range([0, width]);
    const yScale = scaleLinear().range([height, 0]);
    const yRange = yMax - yMin;
    const yPadding = yRange * 0.02;

    xScale.domain([min(convertedDistance), max(convertedDistance)]);
    yScale.domain([yMin - yPadding, yMax + yPadding]);

    this.setState({
      height,
      width,
      xScale,
      yScale,
      stream: {
        elevation: elevationOrDefault,
        convertedElevation,
        distance,
        convertedDistance,
        surface
      }
    });
  }

  gradeAtIndex(index) {
    // Taken from charts.js: 1689 but extending the window to 5 data points
    const { stream } = this.state;
    const { elevation, distance } = stream;
    const maxIndex = distance.length - 1;
    const threshold = 2;

    let streamIndex = index;

    if (streamIndex < threshold) {
      streamIndex = threshold;
    }
    if (maxIndex - streamIndex < threshold) {
      streamIndex = maxIndex - threshold;
    }

    const distanceDiff =
      distance[streamIndex + threshold] - distance[streamIndex - threshold];
    const elevationDiff =
      elevation[streamIndex + threshold] - elevation[streamIndex - threshold];
    const grade = (elevationDiff * 100) / distanceDiff;

    return grade;
  }

  scopeTo(scopeTo) {
    const { stream, xScale, yScale } = this.state;

    if (!stream) {
      return;
    }

    const start = scopeTo ? scopeTo[0] : 0;
    const end = scopeTo ? scopeTo[1] : stream.distance.length - 1;

    // Taken from directory/ElevationChart.js.coffee: 263
    const range = end - start;
    // the padding on each side of the selected area should ideally be
    // 1/3 of the area itself, but this can't be the case if the start
    // or the end are too close to the edges
    const padding = parseInt(range / 3, 10);
    const enoughSpaceAtStart = start - padding <= 0;
    const enoughSpaceAtEnd = end + padding > stream.distance.length - 1;

    const paddedStart = enoughSpaceAtStart
      ? parseInt(start / 3, 10)
      : start - padding;

    const paddedEnd = enoughSpaceAtEnd
      ? parseInt(end + (stream.distance.length - 1 - end) / 3, 10)
      : end + padding;

    const newXScale = scaleLinear()
      .range(xScale.range())
      .domain([
        stream.convertedDistance[paddedStart],
        stream.convertedDistance[paddedEnd]
      ]);

    const croppedElevationStream = stream.convertedElevation.slice(
      paddedStart,
      paddedEnd + 1
    );
    const yMax = max(croppedElevationStream);
    const yMin = min(croppedElevationStream);
    const yRange = yMax - yMin;
    const yPadding = yRange * 0.02;
    const newYScale = scaleLinear()
      .range(yScale.range())
      .domain([yMin - yPadding, yMax + yPadding]);

    this.setState({ xScale: newXScale, yScale: newYScale });
  }

  render() {
    const {
      margin,
      scopeTo,
      scopeColor,
      areaColor,
      highlightArea,
      highlightAreaColor,
      className
    } = this.props;
    const {
      stream,
      xScale,
      yScale,
      width,
      height,
      markerPosition,
      tooltipContent
    } = this.state;

    return (
      <div className={className} ref={this.container}>
        {height > 0 && width > 0 && (
          <svg
            ref={this.node}
            width={width + margin.left + margin.right}
            height={height + margin.top + margin.bottom}
          >
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              <Axis
                xScale={xScale}
                yScale={yScale}
                height={height}
                width={width}
                formatters={this.formatters}
                scopeTo={scopeTo}
              />

              {stream?.distance?.length > 0 && (
                <>
                  <Area
                    fill={areaColor}
                    xScale={xScale}
                    yScale={yScale}
                    height={height}
                    stream={stream}
                    clipPath="url(#clipArea)"
                  />

                  {stream.surface && (
                    <SurfaceTypePath
                      xScale={xScale}
                      yScale={yScale}
                      stream={stream}
                      clipPath="url(#clipArea)"
                    />
                  )}
                </>
              )}

              {scopeTo && (
                <Area
                  fill={scopeColor}
                  xScale={xScale}
                  yScale={yScale}
                  height={height}
                  stream={{
                    convertedElevation: stream.convertedElevation.slice(
                      scopeTo[0],
                      scopeTo[1]
                    ),
                    convertedDistance: stream.convertedDistance.slice(
                      scopeTo[0],
                      scopeTo[1]
                    )
                  }}
                />
              )}

              {highlightArea && (
                <Area
                  fill={highlightAreaColor}
                  xScale={xScale}
                  yScale={yScale}
                  height={height}
                  stream={{
                    convertedElevation: stream.convertedElevation.slice(
                      highlightArea[0],
                      highlightArea[1]
                    ),
                    convertedDistance: stream.convertedDistance.slice(
                      highlightArea[0],
                      highlightArea[1]
                    )
                  }}
                />
              )}

              <clipPath id="clipArea">
                <rect
                  x={0}
                  y={-margin.top}
                  height={height + margin.top}
                  width={width}
                />
              </clipPath>

              <g>
                <rect
                  className={styles.hitbox}
                  ref={this.hitbox}
                  x={0}
                  y={-margin.top}
                  height={height + margin.top}
                  width={width}
                  onMouseMove={this.updateMarker}
                  onMouseLeave={this.handleMouseLeave}
                />
                {stream.distance.length > 0 &&
                  markerPosition &&
                  tooltipContent && (
                    <Tooltip
                      position={markerPosition}
                      content={tooltipContent}
                      clipPath="url(#clipArea)"
                      chartWidth={width}
                      chartMargin={margin}
                    />
                  )}
              </g>
            </g>
          </svg>
        )}
      </div>
    );
  }
}

ElevationChart.propTypes = {
  height: PropTypes.number.isRequired,
  elevation: PropTypes.arrayOf(PropTypes.number).isRequired,
  distance: PropTypes.arrayOf(PropTypes.number).isRequired,
  surface: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  units: PropTypes.string.isRequired,
  margin: PropTypes.shape({
    top: PropTypes.number.isRequired,
    right: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired
  }),
  scopeTo: PropTypes.arrayOf(PropTypes.number),
  scopeColor: PropTypes.string,
  areaColor: PropTypes.string,
  highlightArea: PropTypes.arrayOf(PropTypes.number),
  highlightAreaColor: PropTypes.string,
  onUpdateMarker: PropTypes.func,
  onMouseLeave: PropTypes.func,
  hoveredDistance: PropTypes.number,
  formatters: PropTypes.shape().isRequired,
  tooltipDistanceLabel: PropTypes.string,
  tooltipElevationLabel: PropTypes.string,
  tooltipGradeLabel: PropTypes.string,
  tooltipSurfaceLabel: PropTypes.string,
  tooltipSurfaceTypes: PropTypes.shape({
    paved: PropTypes.string.isRequired,
    unpaved: PropTypes.string.isRequired,
    unknown: PropTypes.string.isRequired
  }),
  className: PropTypes.string
};

ElevationChart.defaultProps = {
  margin: {
    top: 10,
    right: 24,
    bottom: 20,
    left: 45
  },
  surface: null,
  scopeTo: null,
  scopeColor: 'rgb(23, 90, 189)',
  areaColor: 'rgba(100, 100, 100, 0.66)',
  highlightArea: null,
  highlightAreaColor: color.colorCoreO3,
  onUpdateMarker: () => null,
  onMouseLeave: () => null,
  hoveredDistance: null,
  tooltipDistanceLabel: 'Distance',
  tooltipElevationLabel: 'Elevation',
  tooltipGradeLabel: 'Grade',
  tooltipSurfaceLabel: 'Surface Type',
  tooltipSurfaceTypes: {
    paved: 'Paved',
    unpaved: 'Unpaved',
    unknown: 'Unknown'
  },
  className: ''
};

export default ElevationChart;
